"use client";

import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { InfoIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useUserSettingsStore } from "@/lib/zustand";
import { useEffect } from "react";

const userSettingsSchema = z.object({
  transactionSpeed: z.enum(["default", "instant", "standard", "fast"]),
  slippageTolerance: z.enum(["0.001", "0.005", "0.01", "custom"]),
  customSlippage: z.string().optional(),
});

type UserSettingsFormValues = z.infer<typeof userSettingsSchema>;

export const UserSettingsForm = () => {
  const { userSettings, setUserSettings } = useUserSettingsStore();

  const form = useForm<UserSettingsFormValues>({
    resolver: zodResolver(userSettingsSchema),
    defaultValues: {
      transactionSpeed: userSettings.speed as
        | "default"
        | "instant"
        | "standard"
        | "fast",
      slippageTolerance: userSettings.slippage as
        | "custom"
        | "0.001"
        | "0.005"
        | "0.01",
      customSlippage: userSettings.slippage,
    },
  });

  const watchSlippage = form.watch("slippageTolerance");
  const watchSpeed = form.watch("transactionSpeed");

  useEffect(() => {
    const subscription = form.watch((value) => {
      if (value.transactionSpeed && value.slippageTolerance) {
        // console.log("settings", value);
        setUserSettings({
          speed: value.transactionSpeed,
          slippage:
            value.slippageTolerance === "custom"
              ? value.customSlippage || "0.005"
              : value.slippageTolerance,
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [form.watch, setUserSettings]);

  // useEffect(() => {
  //   console.log("userSettings zustand", userSettings);
  // }, [userSettings]);

  return (
    <Form {...form}>
      <form className="space-y-6">
        {/* Transaction Speed Section */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Transaction Speed
            </span>
            <InfoIcon className="w-4 h-4 text-muted-foreground" />
          </div>
          <FormField
            control={form.control}
            name="transactionSpeed"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex gap-2 overflow-x-auto scrollbar-none"
                  >
                    {[
                      { value: "default", label: "Default" },
                      { value: "fast", label: "Fast" },
                      { value: "instant", label: "Instant" },
                    ].map((option) => (
                      <div
                        key={option.value}
                        className={cn(
                          "flex items-center justify-center px-4 py-0.5 rounded-md border border-border",
                          field.value === option.value
                            ? "bg-primary text-background"
                            : "bg-card border border-primary hover:bg-primary/20 text-primary hover:text-foreground"
                        )}
                      >
                        <RadioGroupItem
                          value={option.value}
                          className="hidden"
                          id={option.value}
                        />
                        <label
                          htmlFor={option.value}
                          className="text-sm cursor-pointer"
                        >
                          {option.label}
                        </label>
                      </div>
                    ))}
                  </RadioGroup>
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        {/* Slippage Tolerance Section */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Slippage Tolerance
            </span>
            <InfoIcon className="w-4 h-4 text-muted-foreground" />
          </div>
          <FormField
            control={form.control}
            name="slippageTolerance"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex gap-2 overflow-x-auto scrollbar-none"
                  >
                    {[
                      { value: "0.001", label: "0.1%" },
                      { value: "0.005", label: "0.5%" },
                      { value: "0.01", label: "1.0%" },
                      { value: "custom", label: "Set Custom" },
                    ].map((option) => (
                      <div
                        key={option.value}
                        className={cn(
                          "flex items-center justify-center px-4 py-0.5 rounded-md border border-border",
                          field.value === option.value
                            ? "bg-primary text-background"
                            : "bg-card border border-primary hover:bg-primary/20 text-primary hover:text-foreground"
                        )}
                      >
                        <RadioGroupItem
                          value={option.value}
                          className="hidden"
                          id={`slippage-${option.value}`}
                        />
                        <label
                          htmlFor={`slippage-${option.value}`}
                          className="text-sm cursor-pointer"
                        >
                          {option.label}
                        </label>
                      </div>
                    ))}
                  </RadioGroup>
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        {/* Custom Slippage Input */}
        {watchSlippage === "custom" && (
          <FormField
            control={form.control}
            name="customSlippage"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    placeholder="Enter custom slippage %"
                    className="w-full"
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value !== "" && !isNaN(Number(value))) {
                        field.onChange(Number(value));
                      }
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )}
      </form>
    </Form>
  );
};
